import React, { FC } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import ColorsWrapper from '@styles/colors';
import { scale as initialScale } from '@components/frame';
import { isPhone } from '@helpers';
import Svg from '@styles/icons/svg';
import { font as initialFont } from '@styles/fonts';
import { ColorFunction } from '@styles/colors/types';
import { RetrievedDocumentReferenceLink } from '@v2/components/imageHandling/types/types';
import { ScaleFunction } from '@components/operatorPanel/interfaces';
import { useManageAttachItem } from './hooks/useManageAttachItem';
import { ATTACH_ITEM_STYLES, ICON_TEXT_GAP } from '@v2/components/imageHandling/components/Header/constants';
import { SimpleOnPress } from '@interfaces/base';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';

export interface AttachItemProps extends SimpleOnPress {
  C: ColorFunction;
  link: RetrievedDocumentReferenceLink;
  onToggleFavorite: () => void;
  onRemove: () => void;
  noBg?: boolean;
  isLast?: boolean;
  scale?: ScaleFunction;
  font?: typeof initialFont;
}

const AttachItem: FC<AttachItemProps> = ({
  C,
  link,
  onPress,
  onToggleFavorite,
  onRemove,
  noBg = false,
  isLast = false,
  scale = initialScale,
  font = initialFont,
}) => {
  const { categoryKey, categoryName, handlePress, isFavorite, referenceName } = useManageAttachItem({ link, onPress });

  return (
    <>
      <TouchableOpacity onPress={handlePress} activeOpacity={0.8} style={{ width: '100%', justifyContent: 'center' }}>
        <View
          style={{
            width: '100%',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: noBg ? 'transparent' : C('Background/Light/Field on modal highlight'),
            borderRadius: scale(ATTACH_ITEM_STYLES.borderRadius),
            paddingHorizontal: scale(ATTACH_ITEM_STYLES.paddingHorizontal),
            height: scale(ATTACH_ITEM_STYLES.height),
            gap: scale(10),
            borderWidth: StyleSheet.hairlineWidth,
            borderColor: C('Hairline/Light/No trans'),
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: scale(ICON_TEXT_GAP), flex: 1 }}>
            <TouchableOpacity
              testID={TEST_IDS.documentsAttachModal.itemRemoveButton(link.scopeId, link.scopeReferenceId)}
              onPress={onRemove}
              style={{ gap: scale(isPhone ? 8 : 12) }}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Svg icon="clipMinus" width={scale(24)} height={scale(24)} color={isPhone ? 'Link/Light/Primary' : 'Label/Light/Primary'} />
            </TouchableOpacity>

            {/* Left text & link arrow */}
            <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
              <Text numberOfLines={1} style={[font['Button 1'], { color: C('Label/Light/Primary'), marginRight: scale(4) }]}>
                {categoryName}
              </Text>
              <View style={{ flexDirection: 'row', alignItems: 'center', flex: 1 }}>
                <Text style={[font['Button 1'], { color: C('Link/Light/Primary') }]} numberOfLines={1} ellipsizeMode="tail">
                  {referenceName}
                </Text>
                {!['Machine', 'UserAccount'].includes(categoryKey) && (
                  <Svg icon="arrowLight" width={scale(20)} height={scale(20)} color="Link/Light/Primary" style={{ marginLeft: scale(-1) }} />
                )}
              </View>
            </View>
          </View>

          {/* Right controls */}
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'flex-end', gap: scale(18) }}>
            <TouchableOpacity
              testID={TEST_IDS.documentsAttachModal.itemFavoriteButton(link.scopeId, link.scopeReferenceId)}
              onPress={onToggleFavorite}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Svg
                icon={isFavorite ? 'starSelected' : 'starUnselected'}
                width={scale(21)}
                height={scale(21)}
                color={isPhone ? 'Link/Light/Primary' : isFavorite ? 'Label/Light/Primary' : 'Label/Light/Tertiary'}
              />
            </TouchableOpacity>
          </View>
        </View>
      </TouchableOpacity>
      {/* Border bottom line - only if not last item */}
      {isPhone && !isLast && (
        <View
          style={{
            height: StyleSheet.hairlineWidth,
            backgroundColor: C('Hairline/Light/Transpar'),
            marginLeft: scale(15),
          }}
        />
      )}
    </>
  );
};

export default ColorsWrapper(AttachItem);
